import { apiClient, ensureCsrfCookie } from '@/api/client'
import type { ApiSuccessResponse, PaginationMeta } from '@/types/api'

export type JobStatus = 'draft' | 'published' | 'paused' | 'closed' | 'archived'
export type EmploymentType = 'full_time' | 'part_time' | 'internship' | 'contract' | 'freelance'
export type WorkMode = 'onsite' | 'remote' | 'hybrid'
export type ScreeningQuestionType = 'text' | 'yes_no' | 'multiple_choice' | 'dropdown'

export interface ScreeningQuestion {
  id: number
  questionText: string
  questionType: ScreeningQuestionType
  isRequired: boolean
  sortOrder: number
  options: string[]
}

export interface RecruiterJob {
  id: number
  title: string
  numberOfOpenings: number
  description: string
  employmentType: EmploymentType
  workMode: WorkMode
  locationCity: string | null
  locationState: string | null
  locationCountry: string | null
  experienceMinMonths: number | null
  experienceMaxMonths: number | null
  salaryMin: number | null
  salaryMax: number | null
  currency: string | null
  skillsRequired: string[]
  educationRequired: string[]
  noticePeriodMaxDays: number | null
  applicationDeadline: string | null
  status: JobStatus
  applicantCount: number
  jdFileUrl: string | null
  publishedAt: string | null
  postedByUserId: number
  // Empty/undefined = open to every LMS source + the native portal.
  // Only populated on detail/edit responses, not the list view.
  sourceIds?: number[]
  screeningQuestions: ScreeningQuestion[]
  createdAt: string
  updatedAt: string
}

export interface JobSourceOption {
  id: number
  name: string
}

export interface LocationState {
  id: number
  name: string
  code: string
  type: 'state' | 'union_territory'
}

export interface LocationCity {
  id: number
  name: string
}

export interface JobPayload {
  title: string
  number_of_openings?: number
  description: string
  employment_type: EmploymentType
  work_mode: WorkMode
  location_city?: string
  location_state?: string
  location_country?: string
  experience_min_months?: number
  experience_max_months?: number
  salary_min?: number
  salary_max?: number
  currency?: string
  skills_required_json?: string[]
  education_required_json?: string[]
  notice_period_max_days?: number
  application_deadline?: string
  // Omit entirely to leave a job open to every source unchanged; send an
  // empty array to explicitly clear all restrictions.
  source_ids?: number[]
}

export interface ScreeningQuestionPayload {
  question_text: string
  question_type: ScreeningQuestionType
  is_required?: boolean
  sort_order?: number
}

async function withCsrf<T>(fn: () => Promise<T>): Promise<T> {
  await ensureCsrfCookie()
  return fn()
}

export const jobsApi = {
  list: async (params: { status?: string; page?: number; per_page?: number }) => {
    const { data } = await apiClient.get<
      ApiSuccessResponse<RecruiterJob[]> & { meta: PaginationMeta }
    >('/recruiter/jobs', { params })
    return { jobs: data.data, meta: data.meta }
  },

  get: async (jobId: number) => {
    const { data } = await apiClient.get<ApiSuccessResponse<RecruiterJob>>(
      `/recruiter/jobs/${jobId}`,
    )
    return data.data
  },

  create: (payload: JobPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<RecruiterJob>>(
        '/recruiter/jobs',
        payload,
      )
      return data.data
    }),

  update: (jobId: number, payload: JobPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.put<ApiSuccessResponse<RecruiterJob>>(
        `/recruiter/jobs/${jobId}`,
        payload,
      )
      return data.data
    }),

  changeStatus: (jobId: number, status: JobStatus) =>
    withCsrf(async () => {
      const { data } = await apiClient.put<ApiSuccessResponse<RecruiterJob>>(
        `/recruiter/jobs/${jobId}/status`,
        { status },
      )
      return data.data
    }),

  uploadJd: (jobId: number, file: File) =>
    withCsrf(async () => {
      const form = new FormData()
      form.append('jd_file', file)
      const { data } = await apiClient.post<ApiSuccessResponse<RecruiterJob>>(
        `/recruiter/jobs/${jobId}/jd`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return data.data
    }),

  deleteJd: (jobId: number) =>
    withCsrf(async () => {
      const { data } = await apiClient.delete<ApiSuccessResponse<RecruiterJob>>(
        `/recruiter/jobs/${jobId}/jd`,
      )
      return data.data
    }),

  sourceOptions: async () => {
    const { data } = await apiClient.get<ApiSuccessResponse<JobSourceOption[]>>('/recruiter/job-source-options')
    return data.data
  },

  states: async () => {
    const { data } = await apiClient.get<ApiSuccessResponse<LocationState[]>>('/recruiter/locations/states')
    return data.data
  },

  cities: async (stateId: number) => {
    const { data } = await apiClient.get<ApiSuccessResponse<LocationCity[]>>(
      `/recruiter/locations/states/${stateId}/cities`,
    )
    return data.data
  },
}

export const screeningQuestionsApi = {
  create: (jobId: number, payload: ScreeningQuestionPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<ScreeningQuestion>>(
        `/recruiter/jobs/${jobId}/screening-questions`,
        payload,
      )
      return data.data
    }),

  update: (jobId: number, questionId: number, payload: ScreeningQuestionPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.put<ApiSuccessResponse<ScreeningQuestion>>(
        `/recruiter/jobs/${jobId}/screening-questions/${questionId}`,
        payload,
      )
      return data.data
    }),

  delete: (jobId: number, questionId: number) =>
    withCsrf(async () => {
      await apiClient.delete(`/recruiter/jobs/${jobId}/screening-questions/${questionId}`)
    }),
}
